// Topbar.js
import React, {useState} from 'react'
import {
    AppBar,
    Toolbar,
    Container,
    Box,
    Button,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Slide,
    useScrollTrigger,
    useTheme,
    useMediaQuery
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import PrimaryButton from "../Buttons/PrimaryButton";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import Logo from "../../../Images/LogoTop.png"
/**
 * Hides its single child (the AppBar) whenever the page is scrolled down.
 */
function HideOnScroll({children}) {
    const trigger = useScrollTrigger()
    return (
        <Slide appear={false} direction="down" in={!trigger}>
            {children}
        </Slide>
    )
}

export default function Topbar(props) {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    // State for the desktop dropdown
    const [anchorPlan, setAnchorPlan] = useState(null)
    const planOpen = Boolean(anchorPlan)
    const handlePlanOpen = (e) => setAnchorPlan(e.currentTarget)
    const handlePlanClose = () => setAnchorPlan(null)

    // State for the mobile drawer
    const [drawerOpen, setDrawerOpen] = useState(false)
    const toggleDrawer = (open) => () => setDrawerOpen(open)

    // Define your nav items
    const menuItems = [
        {
            label: 'Über mich', onClick: () => {
                props.refs[0].current?.scrollIntoView({behavior: 'smooth'})
            }
        },
        {
            label: 'Reviews', onClick: () => {
                props.refs[1].current?.scrollIntoView({behavior: 'smooth'})

            }
        },
        {
            label: 'The Smart Physique Plan',
            isDropdown: true,
            onClick: handlePlanOpen,
            dropdownItems: [
                {
                    label: 'Was erwartet dich', onClick: () => {
                        handlePlanClose();
                        props.refs[2].current?.scrollIntoView({behavior: 'smooth'})
                    }
                },
                {
                    label: 'Digital Hub', onClick: () => {
                        handlePlanClose()
                        props.refs[3].current?.scrollIntoView({behavior: 'smooth'})

                    }
                },
                {
                    label: 'Kosten', onClick: () => {
                        handlePlanClose()
                        props.refs[4].current?.scrollIntoView({behavior: 'smooth'})

                    }
                },
                {
                    label: 'Kostenloses Erstgespräch', onClick: () => {
                        handlePlanClose()
                        props.refs[5].current?.scrollIntoView({behavior: 'smooth'})

                    }
                }
            ]
        },
        {
            label: 'Einzelleistungen', onClick: () => {
                props.refs[6].current?.scrollIntoView({behavior: 'smooth'})

            }
        },
        {
            label: 'Packages', onClick: () => {
                props.refs[7].current?.scrollIntoView({behavior: 'smooth'})

            }
        }
    ]

    // CTA button
    const cta = {
        label: 'Termin vereinbaren', onClick: () => {
            props.refs[5].current?.scrollIntoView({behavior: 'smooth'})

        }
    }

    return (
        <>
            {/* Only the AppBar is wrapped in HideOnScroll */}
            <HideOnScroll>
                <AppBar
                    position="fixed"
                    elevation={0}
                    sx={{
                        bgcolor: '#fff',
                        borderBottom: '1px solid #e0e0e0',
                        top: 0
                    }}
                >
                    <Container maxWidth="lg">
                        <Toolbar disableGutters sx={{}}>
                            {/* logo only on sm */}
                            {!isMobile && (
                                <Box component="img" src={Logo} alt="Logo" sx={{height: 40,
                                paddingRight:"20px"
                                }}/>
                            )}

                            {isMobile ? (
                                <Box sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    {/* hamburger menu at left */}
                                    <IconButton aria-label="Navigation Menu" edge="start" onClick={toggleDrawer(true)}>
                                        <MenuIcon/>
                                    </IconButton>

                                    {/* calendar CTA at right */}
                                    <IconButton aria-label="Appointment" edge="end" onClick={cta.onClick}>
                                        <CalendarTodayIcon/>
                                    </IconButton>
                                </Box>
                            ) : (
                                // Desktop: full nav + CTA
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexGrow: 1
                                }}>
                                    <Box sx={{display: 'flex', gap: 2}}>
                                        {menuItems.map((item) =>
                                            item.isDropdown ? (
                                                <React.Fragment key={item.label}>
                                                    <Button
                                                        endIcon={<ArrowDropDownIcon/>}
                                                        onClick={item.onClick}
                                                    >
                                                        {item.label}
                                                    </Button>
                                                    <Menu
                                                        anchorEl={anchorPlan}
                                                        open={planOpen}
                                                        onClose={handlePlanClose}
                                                        anchorOrigin={{
                                                            vertical: 'bottom',
                                                            horizontal: 'left'
                                                        }}
                                                        transformOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'left'
                                                        }}
                                                    >
                                                        {item.dropdownItems.map((dd) => (
                                                            <MenuItem
                                                                key={dd.label}
                                                                onClick={dd.onClick}
                                                            >
                                                                {dd.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Menu>
                                                </React.Fragment>
                                            ) : (
                                                <Button
                                                    key={item.label}
                                                    onClick={item.onClick}
                                                >
                                                    {item.label}
                                                </Button>
                                            )
                                        )}
                                    </Box>
                                    <PrimaryButton
                                        variant="contained"
                                        onClick={cta.onClick}
                                    >
                                        {cta.label}
                                    </PrimaryButton>
                                </Box>

                            )}
                        </Toolbar>
                    </Container>
                </AppBar>
            </HideOnScroll>

            {/* Mobile drawer (outside HideOnScroll) */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
            >
                <Box
                    sx={{width: 240}}
                    role="presentation"
                    onClick={toggleDrawer(false)}
                    onKeyDown={toggleDrawer(false)}
                >
                    <List>
                        {menuItems.map((item) => (
                            <React.Fragment key={item.label}>
                                <ListItem disablePadding>
                                    <ListItemButton onClick={item.onClick}>
                                        <ListItemText primary={item.label}/>
                                    </ListItemButton>
                                </ListItem>
                                {item.isDropdown &&
                                    item.dropdownItems.map((dd) => (
                                        <ListItem
                                            key={dd.label}
                                            disablePadding
                                            sx={{pl: 4}}
                                        >
                                            <ListItemButton onClick={dd.onClick}>
                                                <ListItemText primary={dd.label}/>
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                            </React.Fragment>
                        ))}

                        {/* CTA inside drawer */}
                        <ListItem disablePadding>
                            <ListItemButton onClick={cta.onClick}>
                                <ListItemText
                                    primary={cta.label}
                                    primaryTypographyProps={{
                                        color: 'primary',
                                        fontWeight: 'bold'
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Box>
            </Drawer>
        </>
    )
}
